import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, FormHelperText, useTheme } from "@mui/material";
import { DocumentUpload } from "@components/FileUpload/FileUpload";
import { useFileUploadProfiles } from "@components/FileUpload/FileUpload.constants";
import SingleFile from "@components/FileUpload/SingleFile";
import { useUploadStore, type UploadEntry } from "@components/FileUpload/uploadStore";
import CustomFormLabel from "@components/FormItem/FormItem.styles";
import Input from "@components/Input/Input";
import { getSideDrawerStyles } from "@components/SideDrawer/SideDrawer.styles";
import SideDrawer from "@components/SimpleSideDrawer/SideDrawer";
import {
  getFileSignature,
  isPrintableKey,
  makeInputSlotProps,
  makeInputSxProps,
  TEXTAREA_MAX_CHARS,
} from "@features/vessel-call/vessel-call-status/MovementServices/form/utils";
import { uploadFileToAzureBlob } from "@services/azureDataApi";
import { SasTokenPrefix, SasTokenPrivilege } from "@services/user/types";
import { getSasToken } from "@services/userApi";
import { useMutation } from "@tanstack/react-query";
import { runTransaction } from "@telemetry/runTransaction";
import { useTranslation } from "react-i18next";

interface CancelVesselCallDrawerProps {
  isOpen: boolean;
  onClose: (drawerName: string) => void;
  onSubmit?: (payload?: any) => void;
}

const LANG_BASE = "vessel-call";
const ATTACHMENTS_FIELD = "cancelVesselCallAttachments";
const MAX_ATTACHMENTS = 10;
const EMPTY_UPLOAD_ENTRIES: UploadEntry[] = [];

interface UploadedCancelAttachment {
  documentUrl: string;
  documentType: string;
  documentName: string;
  fileSignature: string;
}

const CancelVesselCallDrawer: React.FC<CancelVesselCallDrawerProps> = ({
  isOpen,
  onClose,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [reason, setReason] = useState("");
  const [reasonError, setReasonError] = useState<string>();
  const [uploadError, setUploadError] = useState("");

  const [error, setError] = useState<string>();
  const uploadedCancelAttachmentsRef = useRef<UploadedCancelAttachment[]>([]);
  const fileProfiles = useFileUploadProfiles();
  const uploadProfile = fileProfiles.sideDrawerApprovalFile;
  const attachments = useUploadStore(
    (state) => state.filesByKey[ATTACHMENTS_FIELD] ?? EMPTY_UPLOAD_ENTRIES,
  );
  const setFilesForKey = useUploadStore((state) => state.setFilesForKey);
  const clearFilesForKey = useUploadStore((state) => state.clearFilesForKey);
  const styles = getSideDrawerStyles(theme);
  const inputSlotProps = makeInputSlotProps(theme);
  const sxProps = makeInputSxProps(theme);
  const allowedExtensions = uploadProfile.format.map((extension) =>
    extension.startsWith(".") ? extension.toLowerCase() : `.${extension.toLowerCase()}`,
  );

  const validateReason = (value: string) => {
    const label = t(`${LANG_BASE}.cancel.reason`);

    if (!value.trim()) {
      return t("form-validation.required", { label });
    }

    if (value.length > TEXTAREA_MAX_CHARS) {
      return t("form-validation.max-length", { label, max: TEXTAREA_MAX_CHARS });
    }

    return undefined;
  };

  const documentUploadMutation = useMutation({
    mutationFn: ({ file, documentType }: { file: File; documentType: string }) =>
      runTransaction(
        {
          module: "vessel_call",
          screen: "CancelVesselCallDrawer",
          action: "upload_vessel_call_document",
          entityType: "vessel_call_document",
        },
        async (transactionId) => {
          const payloadForSasToken = {
            prefix: SasTokenPrefix.VESSEL_REGISTRATION,
            contentType: file.type || null,
            privilege: SasTokenPrivilege.WRITE,
            blobUrl: null,
          };

          const { blobUrl, sasToken } = await getSasToken(
            payloadForSasToken,
            ...(transactionId ? [transactionId] : []),
          );
          const [uploadResult] = await uploadFileToAzureBlob({
            file,
            blobUrl,
            sasToken,
            certificateType: documentType,
          });

          return {
            documentUrl: uploadResult?.documentUrl ?? blobUrl,
            documentType,
            documentName: file.name,
            fileSignature: getFileSignature(file) ?? file.name,
          };
        },
      ),
    onSuccess: (uploadedAttachment) => {
      setError(undefined);
      uploadedCancelAttachmentsRef.current = [
        ...uploadedCancelAttachmentsRef.current.filter(
          (attachment) => attachment.fileSignature !== uploadedAttachment.fileSignature,
        ),
        uploadedAttachment,
      ];
    },
    onError: (uploadError: Error) => setError(uploadError.message),
  });

  const handleDocumentUpload = (file: File, documentType: string) => {
    documentUploadMutation.mutate({ file, documentType });
  };

  const setCancelAttachmentFile = useCallback(
    (files: UploadEntry[]) => {
      if (!files.length) {
        clearFilesForKey(ATTACHMENTS_FIELD);
        return;
      }

      setFilesForKey(ATTACHMENTS_FIELD, files, {
        mode: "replace",
        maxFiles: MAX_ATTACHMENTS,
      });
    },
    [clearFilesForKey, setFilesForKey],
  );

  const resetFormValues = useCallback(() => {
    setReason("");
    setReasonError(undefined);
    setUploadError("");
    setError(undefined);
    uploadedCancelAttachmentsRef.current = [];
    setCancelAttachmentFile([]);
  }, [setCancelAttachmentFile]);

  useEffect(() => {
    if (!isOpen) {
      resetFormValues();
    }
  }, [isOpen, resetFormValues]);

  const handleClose = () => {
    resetFormValues();
    onClose("cancelDrawer");
  };

  const handleSubmit = () => {
    const error = validateReason(reason);
    setReasonError(error);

    if (error) return;

    const payload = {
      attachments: uploadedCancelAttachmentsRef.current,
      reason,
    };

    onSubmit?.(payload);
  };

  const validateUploadFiles = useCallback(
    (incomingFiles: File[]) => {
      const existingFilesCount =
        useUploadStore.getState().getFilesForKey(ATTACHMENTS_FIELD).length ?? 0;

      if (existingFilesCount + incomingFiles.length > MAX_ATTACHMENTS) {
        return t("form-validation.max-files", { max: MAX_ATTACHMENTS });
      }

      return null;
    },
    [t],
  );

  const handleValidationResult = useCallback((message: string | null) => {
    setUploadError(message ?? "");
  }, []);

  return (
    <div data-testid="cancel-vessel-call">
      <SideDrawer
        isOpen={isOpen}
        title={t(`${LANG_BASE}.cancel.title`)}
        onClose={handleClose}
        onCancel={handleClose}
        submitLabel={t(`${LANG_BASE}.cancel.cancel-vessel-call-button`)}
        onSubmit={handleSubmit}
        keepMounted={false}
      >
        <div className="w-[400px] max-w-full px-6 py-2">
          <div className="flex flex-col gap-2" data-testid="cancel-vessel-call-reason">
            <CustomFormLabel>{t(`${LANG_BASE}.cancel.reason`)}</CustomFormLabel>
            <Input
              placeholder={t(`${LANG_BASE}.cancel.reason`)}
              multiline
              minRows={4}
              fullWidth
              variant="outlined"
              error={Boolean(reasonError)}
              value={reason}
              onKeyDown={(event) => {
                if (!isPrintableKey(event) || reason.length < TEXTAREA_MAX_CHARS) return;

                setReasonError(
                  t("form-validation.max-length", {
                    label: t(`${LANG_BASE}.cancel.reason`),
                    max: TEXTAREA_MAX_CHARS,
                  }),
                );
              }}
              onBlur={() => setReasonError(validateReason(reason))}
              onChange={(event) => {
                const value = event.target.value;
                setReason(value);
                setReasonError(validateReason(value));
              }}
              slotProps={inputSlotProps}
              sx={sxProps}
              testId="cancel-vessel-call-reason-input"
            />
            {reasonError && (
              <p style={styles.errorText} className="text-sm mt-1">
                {reasonError}
              </p>
            )}
            <FormHelperText
              sx={{
                ml: 0,
                mt: 0.5,
                fontSize: 12,
                textAlign: "right",
                color: "text.secondary",
              }}
            >
              {reason.length}/{TEXTAREA_MAX_CHARS}
            </FormHelperText>
          </div>

          <div className="mt-6 flex flex-col gap-4">
            <DocumentUpload
              // key={isOpen ? "open" : "closed"}
              label={t(`${LANG_BASE}.cancel.attachments`)}
              testId="cancel-vessel-call-attachments-upload"
              name={ATTACHMENTS_FIELD}
              isError={Boolean(uploadError)}
              errorMessage={uploadError}
              maxFiles={MAX_ATTACHMENTS}
              allowedExtensions={allowedExtensions}
              conditions={uploadProfile}
              files={attachments}
              setFiles={setCancelAttachmentFile}
              onUpload={(files) => {
                const existingSignatures = new Set(
                  attachments
                    .map((attachment) => getFileSignature(attachment))
                    .filter((signature): signature is string => Boolean(signature)),
                );

                setCancelAttachmentFile(files);
                files
                  .filter((file) => !existingSignatures.has(getFileSignature(file) ?? file.name))
                  .forEach((file) => handleDocumentUpload(file, "CANCEL_ATTACHMENTS"));
              }}
              disabled={documentUploadMutation.isPending}
              uploadDescription={t("upload.dragOrDropDescription")}
              validateDrop={(selectedCount) => {
                if (selectedCount > MAX_ATTACHMENTS) {
                  return t("form-validation.max-files", {
                    max: MAX_ATTACHMENTS,
                  });
                }

                return null;
              }}
              validateFiles={validateUploadFiles}
              onValidationResult={handleValidationResult}
              onReject={() => setUploadError("")}
            />
            {attachments.map((file, index) => (
              <SingleFile
                key={`${file.name}-${index}`}
                testId={`cancel-vessel-call-attachment-${index}`}
                file={file}
                fieldName={ATTACHMENTS_FIELD}
                currentFileIndex={index}
                setFiles={() => {
                  const fileSignature = getFileSignature(file) ?? file.name;
                  uploadedCancelAttachmentsRef.current =
                    uploadedCancelAttachmentsRef.current.filter(
                      (attachment) => attachment.fileSignature !== fileSignature,
                    );
                }}
                isLoading={documentUploadMutation.isPending && file instanceof File}
              />
            ))}
          </div>
        </div>
      </SideDrawer>
      {error ? <Alert severity="error">{error}</Alert> : null}
    </div>
  );
};

export default CancelVesselCallDrawer;
